import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { contentAPI } from '../utils/api';
import { track } from '../utils/analytics';

/**
 * Newsletter signup styled as a dramatic full-width black band,
 * inspired by Mother Wolf / Bavel. Sits right before the footer.
 */
const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [copy, setCopy] = useState({});

  useEffect(() => {
    contentAPI.getNewsletterCopy().then((d) => setCopy(d || {}));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await contentAPI.subscribeNewsletter({ email, source: 'section' });
      if (res.already) toast.info('You are already subscribed.');
      else {
        toast.success(copy.success_message || 'Welcome to Brick Lane. Check your email for confirmation.');
        // Fire CompleteRegistration across Meta Pixel / GA4 / TikTok / PostHog
        // (CAPI handles the server-side counterpart).
        track('CompleteRegistration', { content_name: 'Newsletter', source: 'section' });
      }
      setEmail('');
    } catch (err) {
      toast.error('Could not subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="py-24 md:py-32 bg-[#0f0f0e] text-white relative overflow-hidden"
      data-testid="newsletter-section"
    >
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
        }}
      ></div>

      <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
        <p
          className="text-[10px] md:text-xs tracking-[0.45em] uppercase text-[#C9A86C] mb-4"
          style={{ fontFamily: 'var(--font-serif)' }}
        >
          The List
        </p>
        <h2
          className="text-4xl md:text-6xl mb-6 leading-tight text-white"
          style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, color: '#ffffff' }}
        >
          {copy.section_heading || 'Stay Connected with Brick Lane'}
        </h2>
        <div className="w-16 h-px bg-[#C9A86C] mx-auto mb-8"></div>
        <p className="text-white/70 text-base md:text-lg mb-12 max-w-xl mx-auto leading-relaxed">
          {copy.section_body ||
            'Be the first to hear about new menu items, special events, and exclusive dining experiences.'}
        </p>

        <form onSubmit={handleSubmit} className="max-w-xl mx-auto" data-testid="newsletter-section-form">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              disabled={loading}
              className="flex-1 px-5 py-4 bg-transparent border border-white/20 focus:border-[#C9A86C] outline-none transition-colors text-white placeholder-white/40"
              data-testid="newsletter-section-email"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-4 bg-[#C9A86C] text-black font-semibold tracking-[0.2em] uppercase text-xs hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="newsletter-section-submit"
            >
              {loading ? 'Subscribing…' : (copy.section_button_label || 'Subscribe')}
              {!loading && <ArrowRight size={16} />}
            </button>
          </div>
          <p className="text-white/40 text-xs text-center mt-6 tracking-wide">
            {copy.section_privacy_note || 'We respect your privacy. Unsubscribe anytime.'}
          </p>
        </form>
      </div>
    </section>
  );
};

export default NewsletterSignup;
